// Phase 2 screens: Daily Supply, Directives, Milestones, Salvage Shop, Diagnostics (HUD buttons ->
// overlay), plus Power Cell / boost HUD and reward announcements.
import { D, Decimal, fmt } from '../core/decimal';
import { DAILY_SUPPLY, DIRECTIVE_POOL, DIRECTIVES_BONUS, MILESTONES, MILESTONE_TUNING, SHOP_ITEMS } from '../data/meta';
import { PET_BY_ID } from '../data/pets';
import { itemName } from '../game/equipment';
import type { Game } from '../game/game';
import { describeReward, type RewardResult } from '../game/meta';
import type { MultStat } from '../game/stats';
import type { Renderer } from './renderer';

type Screen = 'daily' | 'directives' | 'milestones' | 'shop' | 'stats';

/** Real-money Gem packs shown in the Toy Shop (src/view/gemStore.ts); absent when the platform isn't wired. */
export interface GemShelf {
  html(): string;
  customHtml(): string;
  buy(sku: string): void;
  setCustomFromSlider(pos: number): void;
}

const $ = (id: string) => document.getElementById(id) as HTMLElement;
/** Desktop landscape browsers get the fixed-size, scaled Toy Shop (phones keep the fluid layout). */
const SHOP_DESKTOP = '(orientation: landscape) and (min-width: 900px) and (min-height: 540px)';
/** Toy Shop design size in CSS px (Royal Toybox v3 mockup proportions); keep in sync with style.css .fixed-design. */
const SHOP_DESIGN = { w: 1120, h: 1000 };
const TITLES: Record<Screen, string> = {
  daily: '🎁 Daily Gift', directives: '📋 Quests', milestones: '🏆 Trophies', shop: 'Toy Shop', stats: '📊 Stats',
};

export class MetaUI {
  private screen: Screen | null = null;
  /** Toy Shop tab: real-money Gem packs, or spending Gems on coins and boosts. */
  private shopTab: 'gems' | 'coins' = 'gems';
  /** Last rendered markup signature; the overlay is rebuilt only when it changes (keeps clicks reliable). */
  private sig = '';

  constructor(private game: Game, private renderer: Renderer, private onChange: () => void, private gems?: GemShelf) {
    document.querySelectorAll<HTMLButtonElement>('[data-open]').forEach((b) =>
      b.addEventListener('click', () => {
        if (b.dataset.shopTab === 'gems' || b.dataset.shopTab === 'coins') this.shopTab = b.dataset.shopTab;
        this.open(b.dataset.open as Screen);
      }));
    $('ov-close').addEventListener('click', () => this.close());
    $('overlay').addEventListener('click', (e) => { if (e.target === $('overlay')) this.close(); });
    $('ov-body').addEventListener('click', (e) => this.onClick(e));
    // The custom Gem slider: update the amount live without rebuilding under the thumb mid-drag.
    $('ov-body').addEventListener('input', (e) => {
      const t = e.target as HTMLInputElement;
      if (!t.classList.contains('cp-range') || !this.gems) return;
      this.gems.setCustomFromSlider(Number(t.value));
      const fresh = document.createElement('div');
      fresh.innerHTML = this.gems.customHtml();
      // Swap the read-outs and button but keep the slider element itself, so the drag isn't interrupted.
      for (const sel of ['.rt-bubble', '.rt-buy']) {
        const now = $('ov-body').querySelector(sel), next = fresh.querySelector(sel);
        if (now && next) now.replaceWith(next);
      }
      const slider = $('ov-body').querySelector<HTMLElement>('.rt-slider'), style = fresh.querySelector<HTMLElement>('.rt-slider')?.style.cssText;
      if (slider && style) slider.style.cssText = style;
      if (this.screen) this.sig = this.render(this.screen); // the next refresh sees no change and leaves the slider alone
    });
    window.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.screen) this.close(); });
    window.addEventListener('resize', () => this.fitShop());

    game.events.on('rewardGranted', (r) => {
      // Supply Drops announce themselves on the battlefield; claims made in screens get a banner too.
      this.renderer.banner(this.summary(r.result), '#7fe0ff', 17);
    });
    game.events.on('boostStarted', () => this.refreshHud());
    setInterval(() => { this.refreshHud(); this.refresh(); }, 250);
    this.refreshHud();
  }

  isOpen(): boolean { return this.screen !== null; }

  /** While a purchase pop-up is up, the gem pill shows this instead of the real balance. */
  private gemHold: number | null = null;
  holdGems(n: number | null): void { this.gemHold = n; this.refreshHud(); }

  open(screen: Screen): void {
    this.screen = screen;
    this.sig = '';
    $('ov-title').textContent = TITLES[screen];
    $('overlay').dataset.screen = screen;
    $('overlay').hidden = false;
    this.refresh();
    this.fitShop();
  }

  /**
   * Desktop landscape: the Toy Shop is laid out once at its design size (SHOP_DESIGN) and scaled as a whole to
   * fit the window, so it looks the same, complete and unscrolled, on every desktop screen.
   */
  private fitShop(): void {
    const card = document.querySelector<HTMLElement>('#overlay .ov-card')!;
    const desktop = this.screen === 'shop' && matchMedia(SHOP_DESKTOP).matches;
    card.classList.toggle('fixed-design', desktop);
    if (!desktop) { card.style.removeProperty('zoom'); return; }
    // Room for the title plaque above the frame and a margin all round.
    const k = Math.min(1, (window.innerWidth - 40) / SHOP_DESIGN.w, (window.innerHeight - 92) / SHOP_DESIGN.h);
    card.style.zoom = k.toFixed(4);
  }

  close(): void {
    this.screen = null;
    $('overlay').hidden = true;
    document.querySelector<HTMLElement>('#overlay .ov-card')?.style.removeProperty('zoom');
  }

  /** Short human text for a granted reward, e.g. "+1.2M coins · +5 💎". */
  summary(r: RewardResult): string {
    const out: string[] = [];
    if (r.scrap.gt(0)) out.push(`+${fmt(r.scrap)} coins`);
    if (r.cells) out.push(`+${r.cells} 💎`);
    if (r.parts) { const p = PET_BY_ID.get(r.parts.petId)!; out.push(`+${r.parts.amount} ${p.name} treats ${p.icon}`); }
    if (r.energy) out.push('⚡ energy');
    if (r.item) out.push(itemName(r.item));
    return out.join(' · ') || 'Reward';
  }

  // ---- HUD ------------------------------------------------------------------------------------

  private refreshHud(): void {
    const g = this.game, m = g.meta;
    $('cells').textContent = String(this.gemHold ?? g.state.cells);
    const dots: Record<Screen, boolean> = {
      daily: m.dailyClaimable(),
      directives: m.directives().some((d) => !d.claimed && d.progress >= DIRECTIVE_POOL.find((p) => p.id === d.id)!.target)
        || m.directivesBonusClaimable(),
      milestones: MILESTONES.some((x) => m.milestoneClaimable(x)),
      shop: false,
      stats: false,
    };
    document.querySelectorAll<HTMLButtonElement>('.meta-btn[data-open]').forEach((b) => {
      (b.querySelector('.dot') as HTMLElement).hidden = !dots[b.dataset.open as Screen];
    });
    const chips: string[] = [];
    if (m.boostActive('damage2x')) chips.push(`<span class="chip">⚡ x2 dmg ${mmss(m.boostLeftSec('damage2x'))}</span>`);
    if (m.boostActive('scrap2x')) chips.push(`<span class="chip">🐷 x2 coins ${mmss(m.boostLeftSec('scrap2x'))}</span>`);
    const html = chips.join('');
    if ($('boost-chips').innerHTML !== html) $('boost-chips').innerHTML = html;
  }

  // ---- Overlay --------------------------------------------------------------------------------

  private refresh(): void {
    if (!this.screen) return;
    const html = this.render(this.screen);
    if (html === this.sig) return;
    this.sig = html;
    $('ov-body').innerHTML = html;
  }

  private render(screen: Screen): string {
    switch (screen) {
      case 'daily': return this.renderDaily();
      case 'directives': return this.renderDirectives();
      case 'milestones': return this.renderMilestones();
      case 'shop': return this.renderShop();
      case 'stats': return this.renderStats();
    }
  }

  private onClick(e: Event): void {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-act]');
    if (!btn || btn.disabled) return;
    const m = this.game.meta, arg = btn.dataset.arg ?? '';
    switch (btn.dataset.act) {
      case 'daily': m.claimDaily(); break;
      case 'directive': m.claimDirective(arg); break;
      case 'bonus': m.claimDirectivesBonus(); break;
      case 'milestone': m.claimMilestone(arg); break;
      case 'buy': if (m.buy(arg)) this.renderer.banner(`Bought ${SHOP_ITEMS.find((i) => i.id === arg)!.name}`, '#ffcc4d', 17); break;
      case 'gempack': this.gems?.buy(arg); break;
      case 'shop-tab': this.shopTab = arg === 'coins' ? 'coins' : 'gems'; break;
    }
    this.sig = '';
    this.refresh();
    this.refreshHud();
    this.onChange();
  }

  private renderDaily(): string {
    const m = this.game.meta, claimable = m.dailyClaimable(), next = m.nextDailyDay();
    const streak = this.game.state.meta.daily.streak;
    const days = DAILY_SUPPLY.map((r, i) => {
      const day = i + 1;
      const got = claimable ? day < next : day <= streak;
      const today = claimable && day === next;
      return `<div class="day ${got ? 'got' : ''} ${today ? 'today' : ''}"><b>Day ${day}</b>${describeReward(r)}${got ? '<br>✔' : ''}</div>`;
    }).join('');
    return `<p class="ov-sub">Come back every day for a bigger gift. Missing a day restarts the streak at Day 1.</p>
      <div class="days">${days}</div>
      <button class="ov-btn" data-act="daily" ${claimable ? '' : 'disabled'} style="width:100%">
        ${claimable ? `Claim Day ${next}` : `Come back in ${hhmm(msToMidnight(this.game.now()))}`}</button>`;
  }

  private renderDirectives(): string {
    const m = this.game.meta;
    const rows = m.directives().map((d) => {
      const def = DIRECTIVE_POOL.find((p) => p.id === d.id)!;
      const pct = Math.min(100, (d.progress / def.target) * 100);
      const ready = !d.claimed && d.progress >= def.target;
      return `<div class="ov-row ${d.claimed ? 'done' : ''}"><div class="ic">📋</div>
        <div class="tx"><b>${def.text.replace('{n}', fmt(def.target))}</b>
          <small>${fmt(d.progress)} / ${fmt(def.target)} · ${describeReward(def.reward)}</small>
          <div class="bar"><i style="width:${pct}%"></i></div></div>
        <button class="ov-btn" data-act="directive" data-arg="${d.id}" ${ready ? '' : 'disabled'}>${d.claimed ? 'Done' : 'Claim'}</button></div>`;
    }).join('');
    const bonusOk = m.directivesBonusClaimable(), bonusDone = this.game.state.meta.directives.bonusClaimed;
    return `<p class="ov-sub">New quests every day at midnight (in ${hhmm(msToMidnight(this.game.now()))}).</p>${rows}
      <div class="ov-row ${bonusDone ? 'done' : ''}"><div class="ic">⭐</div>
        <div class="tx"><b>Complete all quests</b><small>${describeReward(DIRECTIVES_BONUS)}</small></div>
        <button class="ov-btn" data-act="bonus" ${bonusOk ? '' : 'disabled'}>${bonusDone ? 'Done' : 'Claim'}</button></div>`;
  }

  private renderMilestones(): string {
    const m = this.game.meta;
    const tiers = m.tiersClaimed();
    const bonus = Math.pow(1 + MILESTONE_TUNING.damagePerTier, tiers);
    const rows = MILESTONES.map((ms) => {
      const claimed = m.milestoneClaimed(ms), maxed = claimed >= ms.tiers.length;
      const target = ms.tiers[Math.min(claimed, ms.tiers.length - 1)]!;
      const value = m.milestoneValue(ms);
      const pct = maxed ? 100 : Math.min(100, (value / target) * 100);
      const ready = m.milestoneClaimable(ms);
      return `<div class="ov-row ${maxed ? 'done' : ''}"><div class="ic">${ms.icon}</div>
        <div class="tx"><b>${ms.name} <span class="muted">· tier ${claimed}/${ms.tiers.length}</span></b>
          <small>${maxed ? 'All tiers complete' : `${ms.text.replace('{n}', fmt(target))} · ${fmt(Math.min(value, target))} / ${fmt(target)} · +${m.milestoneCells(claimed)} <i class="ic ic-gem"></i> & +${Math.round(MILESTONE_TUNING.damagePerTier * 100)}% damage`}</small>
          <div class="bar"><i style="width:${pct}%"></i></div></div>
        <button class="ov-btn" data-act="milestone" data-arg="${ms.id}" ${ready ? '' : 'disabled'}>${maxed ? 'Done' : 'Claim'}</button></div>`;
    }).join('');
    return `<p class="ov-sub">${tiers} tiers claimed · permanent all damage x${bonus.toFixed(2)}</p>${rows}`;
  }

  private renderShop(): string {
    const cells = this.game.state.cells;
    const tabs = `<div class="shop-tabs" role="tablist">
      <button class="shop-tab ${this.shopTab === 'gems' ? 'on' : ''}" data-act="shop-tab" data-arg="gems" role="tab"><i class="ic ic-gem"></i>Gems</button>
      <button class="shop-tab ${this.shopTab === 'coins' ? 'on' : ''}" data-act="shop-tab" data-arg="coins" role="tab"><i class="coin-ic"></i>Coins</button>
      <span class="shop-wallet"><i class="ic ic-gem"></i><b>${cells.toLocaleString()}</b></span></div>`;
    if (this.shopTab === 'gems') {
      return `${tabs}<div class="rt-banner gems"><b>Power up with Gems.</b><span>Trade them for coins and level up your Guardians in one go.</span></div>
        ${this.gems ? this.gems.html() : '<div class="ov-note">Gem packs for purchase are coming soon.</div>'}`;
    }
    const g = this.game, perKill = g.goldAt(g.state.stage).mul(g.stats.mult('gold'));
    // The coin ladder: each bundle's coins are what it pays at the player's current stage.
    const cards = SHOP_ITEMS.map((i, n) => {
      const kills = i.effect.kind === 'reward' ? i.effect.reward.scrapKills ?? 0 : 0;
      const short = cells < i.cost;
      return `<div class="rt-card rt-coin-card"><span class="rt-name">${i.name}</span>
        <span class="rt-art"><img src="art/shop/shop_coins_${String(n + 1).padStart(2, '0')}.webp" alt=""></span>
        <span class="rt-qty"><b>≈ ${fmt(perKill.mul(kills).floor())}</b><small>coins</small></span>
        ${short ? `<button class="rt-price disabled" data-act="shop-tab" data-arg="gems" title="Not enough Gems: tap to get more"><i class="ic ic-gem"></i>${i.cost.toLocaleString()}</button><button class="rt-get" data-act="shop-tab" data-arg="gems">Get Gems</button>`
          : `<button class="rt-price" data-act="buy" data-arg="${i.id}"><i class="ic ic-gem"></i>${i.cost.toLocaleString()}</button>`}</div>`;
    }).join('');
    const bonus = g.boughtCoinsBonus();
    return `${tabs}<div class="rt-banner coins"><b>Coins.</b><span>Level up your Guardians and Tuck in one go. Bigger bundles give more coins per Gem.</span></div>
      <p class="rt-boost">✨ Coins bought with purchased Gems also boost the Stardust from your next Rewind${bonus > 1.0001 ? ` (this run so far: <b>×${bonus.toFixed(2)}</b>)` : ''}.</p>
      <div class="rt-grid coins">${cards}</div>
      <p class="rt-foot">Coin amounts scale with your stage. Earn free Gems from Daily Gifts, Quests, Trophies and Paper Plane Gifts.</p>`;
  }

  private renderStats(): string {
    const g = this.game, st = g.state, lt = st.lifetime;
    const row = (k: string, v: string) => `<tr><td>${k}</td><td>${v}</td></tr>`;
    const life = [
      row('Best stage', fmt(st.bestStage)), row('Rewinds', fmt(st.ascensions)),
      row('Time played', hhmm(lt.playSec * 1000)), row('Taps', fmt(lt.taps)),
      row('Gloom toys defeated', fmt(lt.kills)), row('Giants toppled', fmt(lt.bossKills)),
      row('Specials used', fmt(lt.casts)), row('Paper Plane Gifts caught', fmt(lt.drops)),
      row('Quests completed', fmt(lt.directives)),
    ].join('');
    const now = [
      row('Tap damage', fmt(g.tapDamageNow())), row('Guardian DPS', fmt(g.heroDpsNow())),
      row('Crit chance', `${(g.critChance() * 100).toFixed(1)}%`), row('Crit damage', `x${fmt(g.critMult())}`),
      row('Giant timer', `${g.bossTimeSec().toFixed(1)}s`), row('Max energy', fmt(g.maxMana())),
    ].join('');
    // Where the damage comes from: product of each source's multipliers per stat.
    const cols: MultStat[] = ['allDamage', 'heroDamage', 'tapDamage', 'gold'];
    const names: Record<string, string> = {
      artifacts: 'Treasures', heroSkills: 'Guardian tricks', skillTree: 'Sticker Book',
      equipment: 'Gear', pets: 'Pals', milestones: 'Trophies',
    };
    const breakdown = g.stats.breakdown().map((src) => {
      const cells = cols.map((c) => {
        let v: Decimal = D(1);
        for (const [s, m] of src.mults) if (s === c) v = v.mul(m);
        return `<td>${v.eq(1) ? '—' : 'x' + (v.lt(1000) ? v.toNumber().toFixed(2) : fmt(v))}</td>`;
      }).join('');
      return `<tr><td>${names[src.id] ?? src.id}</td>${cells}</tr>`;
    }).join('');
    return `<table class="stats"><tr><th>Right now</th><th></th></tr>${now}</table>
      <table class="stats"><tr><th>Lifetime</th><th></th></tr>${life}</table>
      <table class="stats"><tr><th>Multiplier source</th><th>All dmg</th><th>Guardian dmg</th><th>Tap</th><th>Coins</th></tr>${breakdown}</table>`;
  }
}

function msToMidnight(now: number): number {
  const d = new Date(now);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime() - now;
}

function hhmm(ms: number): string {
  const m = Math.floor(ms / 60000);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`;
}

function mmss(sec: number): string {
  const s = Math.ceil(sec);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

